import { Collapse } from '@mui/material';
import { Chip, Button } from '@coordio/ui';
import { ChevronDown as ExpandMore, ChevronUp as ExpandLess } from 'lucide-react';
import { useState } from 'react';

interface ToolCallIndicatorProps {
  toolName: string;
  arguments: string;
  status?: 'pending' | 'executing' | 'completed' | 'failed';
  result?: string;
}

/**
 * Component for displaying tool call information.
 */
export const ToolCallIndicator = ({
  toolName,
  arguments: args,
  status = 'pending',
  result,
}: ToolCallIndicatorProps) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (): 'green' | 'red' | 'blue' | 'default' => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'executing':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'executing':
        return 'Executing...';
      default:
        return 'Pending';
    }
  };

  return (
    <div className="p-3 mb-2 bg-muted/50 rounded border border-border">
      <div className="flex items-center gap-2 mb-2">
        <Chip
          label={`Tool: ${toolName}`}
          color={getStatusColor()}
          variant="outlined"
        />
        <Chip label={getStatusLabel()} color={getStatusColor()} />
        <div className="flex-grow" />
        <Button variant="ghost" size="icon" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Button>
      </div>
      <Collapse in={expanded}>
        <div className="mt-2">
          <span className="text-xs text-muted-foreground block mb-1">
            Arguments:
          </span>
          <pre className="p-2 bg-card rounded text-xs font-mono overflow-auto">
            {args}
          </pre>
          {result && (
            <>
              <span className="text-xs text-muted-foreground block mt-2 mb-1">
                Result:
              </span>
              <pre className="p-2 bg-card rounded text-xs font-mono overflow-auto">
                {result}
              </pre>
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};
